import React, { useEffect, useMemo, useRef, useState } from 'react';
import { readSubmissionManifest, SubmissionManifest } from '../../utils/submissionManifest';
import { authUtils } from '../../services/auth';
import { apiService } from '../../services/api';
import MultiSubmissionModal, { MultiSubmissionSubmitPayload } from './multi-submission/MultiSubmissionModal';
import { getMultiSubmissionConfig } from './multi-submission/configs';
import { PracticeJobHint } from '../competition/LiveTerminals';
import './Submission.css';

interface SubmissionProps {
  competitionId: number;
  competitionType?: string;
  competitionMode?: 'individual' | 'team';
  teamName?: string | null;
  isTeamLead?: boolean;
  teamLeadUsername?: string | null;
  /** Called when an upload starts a practice run, so the live card can seed instantly. */
  onPracticeStarted?: (hint: PracticeJobHint) => void;
  /** Called on a 429 "already running" so the live card refetches and reveals the run. */
  onPracticeConflict?: () => void;
  /** Bump to clear the inline "already running" warning (e.g. after a cancel). */
  clearNoticeSignal?: number;
  /** Read-only preview for hosts: the form shows, but nothing can be submitted. */
  previewOnly?: boolean;
}

const Submission: React.FC<SubmissionProps> = ({
  competitionId,
  competitionType,
  competitionMode = 'individual',
  teamName,
  isTeamLead = true,
  teamLeadUsername,
  onPracticeStarted,
  onPracticeConflict,
  clearNoticeSignal,
  previewOnly = false,
}) => {
  const [file, setFile] = useState<File | null>(null);
  const [submissionUrl, setSubmissionUrl] = useState('');
  const [uploading, setUploading] = useState(false);
  const [uploadError, setUploadError] = useState('');
  const [uploadSuccess, setUploadSuccess] = useState(false);
  const [practiceNotice, setPracticeNotice] = useState<{ kind: 'warning' | 'success'; text: string } | null>(null);
  const [isMultiModalOpen, setIsMultiModalOpen] = useState(false);
  // What the selected zip declares. Read locally, before upload: the practice
  // and graded bundles differ by two lines and picking the wrong one costs a
  // submission that cannot be handed back once the run starts.
  const [manifest, setManifest] = useState<SubmissionManifest | null>(null);
  // Which file the in-flight parse belongs to. Pick a large zip then a small
  // one and the slower parse can land last, labelling the wrong file.
  const fileRef = useRef<File | null>(null);
  // The native input is uncontrolled: setFile(null) does not clear it, so
  // after a successful upload it kept displaying a file that Submit was
  // already disabled for.
  const inputRef = useRef<HTMLInputElement | null>(null);

  // A cancel elsewhere (or any parent-driven signal) clears the "already
  // running" warning, since a new practice upload is allowed again.
  useEffect(() => {
    setPracticeNotice((prev) => (prev && prev.kind === 'warning' ? null : prev));
  }, [clearNoticeSignal]);
  const normalizedCompetitionType = (competitionType || '').toLowerCase();
  const isHtmlCompetition = normalizedCompetitionType === 'html';
  const isGithubCompetition = normalizedCompetitionType === 'github';
  const isUrlCompetition = isHtmlCompetition || isGithubCompetition;
  const isMultiCompetition = normalizedCompetitionType === 'multi';
  const multiConfig = useMemo(
    () => getMultiSubmissionConfig(competitionId),
    [competitionId]
  );

  const getErrorMessage = (err: unknown): string => {
    const maybeApiError = err as { response?: { data?: { detail?: string } }; message?: string };
    return maybeApiError?.response?.data?.detail || maybeApiError?.message || 'Unknown error';
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files.length > 0) {
      const picked = e.target.files[0];
      fileRef.current = picked;
      setFile(picked);
      setUploadError('');
      setUploadSuccess(false);
      setPracticeNotice(null);
      setManifest(null);
      // Fire and forget: the label is a courtesy, so a zip we cannot read just
      // means no label. It must never delay or block the upload itself.
      readSubmissionManifest(picked)
        .then((m) => { if (fileRef.current === picked) setManifest(m); })
        .catch(() => { if (fileRef.current === picked) setManifest(null); });
    }
  };

  const handleSubmit = async () => {
    if (previewOnly) return;
    if (isUrlCompetition) {
      if (!submissionUrl.trim()) {
        setUploadError(isGithubCompetition ? 'Please enter a GitHub URL.' : 'Please enter a URL.');
        return;
      }
      try {
        const parsed = new URL(submissionUrl.trim());
        if (!['http:', 'https:'].includes(parsed.protocol)) {
          setUploadError('URL must start with http:// or https://');
          return;
        }
        if (isGithubCompetition && !/(^|\.)github\.com$/i.test(parsed.hostname)) {
          setUploadError('Please enter a valid github.com URL.');
          return;
        }
      } catch {
        setUploadError('Please enter a valid URL.');
        return;
      }
    } else if (!file) {
      setUploadError('No file selected.');
      return;
    }

    setUploading(true);
    setUploadError('');
    setUploadSuccess(false);
    setPracticeNotice(null);
    try {
      const token = authUtils.getStoredToken();
      const formData = new FormData();
      if (isUrlCompetition) {
        formData.append('submission_url', submissionUrl.trim());
      } else if (file) {
        formData.append('file', file);
      }

      const response = await apiService.post(`/submissions/${competitionId}`, formData, {
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'multipart/form-data',
        },
      });

      const resData = response?.data as {
        status?: string;
        practice?: { job_id: string; world?: string; n_runs: number; message?: string };
      };
      if (resData?.status === 'practice' && resData.practice) {
        // Practice run: seed the live terminals card in the same tick and show
        // the practice message as an inline success note (not the generic one).
        const p = resData.practice;
        onPracticeStarted?.({ job_id: p.job_id, n_runs: p.n_runs });
        setPracticeNotice({ kind: 'success', text: p.message || 'Practice run started' });
      } else {
        setUploadSuccess(true);
      }
      setFile(null);
      fileRef.current = null;
      setManifest(null);
      if (inputRef.current) inputRef.current.value = '';
      if (isUrlCompetition) {
        setSubmissionUrl('');
      }
    } catch (err: unknown) {
      const status = (err as { status?: number })?.status;
      if (status === 429) {
        // A practice run is already in progress: surface a warning (not a crash)
        // and nudge the live card to reveal the run that is already going.
        setPracticeNotice({
          kind: 'warning',
          text:
            getErrorMessage(err) ||
            'A practice run is already in progress; watch it below, a new one can start when it finishes',
        });
        onPracticeConflict?.();
      } else {
        setUploadError(`Failed to submit: ${getErrorMessage(err)}`);
      }
      console.error('Submission error:', err);
    } finally {
      setUploading(false);
    }
  };

  const handleMultiSubmit = async (payload: MultiSubmissionSubmitPayload) => {
    if (previewOnly) return;
    setUploading(true);
    setUploadError('');
    setUploadSuccess(false);
    try {
      const token = authUtils.getStoredToken();
      const formData = new FormData();
      formData.append('multi_manifest', JSON.stringify(payload.manifest));
      payload.files.forEach((artifact) => formData.append('multi_files', artifact));

      await apiService.post(`/submissions/${competitionId}`, formData, {
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'multipart/form-data',
        },
      });

      setUploadSuccess(true);
      setIsMultiModalOpen(false);
    } catch (err: unknown) {
      const message = `Failed to submit: ${getErrorMessage(err)}`;
      setUploadError(message);
      console.error('Multi submission error:', err);
      throw err;
    } finally {
      setUploading(false);
    }
  };

  return (
    <div className="submission-section" style={{ marginTop: '2rem' }} data-tour="submit-form">
      <h2>
        {isMultiCompetition
          ? 'Make Submission'
          : isGithubCompetition
            ? 'Submit Your GitHub URL'
            : isHtmlCompetition
              ? 'Submit Your URL'
              : 'Submit Your File'}
      </h2>
      {competitionMode === 'team' && (
        <p style={{ marginBottom: '0.75rem', color: 'var(--text-secondary)' }}>
          Team submission for <strong>{teamName || 'your team'}</strong>.
          {!isTeamLead && teamLeadUsername ? ` Only lead ${teamLeadUsername} can submit.` : ''}
        </p>
      )}
      {isMultiCompetition ? (
        <div className="submission-multi-row">
          <p className="submission-multi-description">
            {multiConfig.description || 'Open the interactive modal and complete the required fields.'}
          </p>
          <button
            className="btn-submit"
            onClick={() => setIsMultiModalOpen(true)}
            disabled={!previewOnly && (uploading || !isTeamLead)}
          >
            {previewOnly ? 'View submission form' : uploading ? 'Submitting...' : 'Submit Project'}
          </button>
        </div>
      ) : isUrlCompetition ? (
        <div className="submission-url-row">
          <input
            type="url"
            value={submissionUrl}
            onChange={(e) => {
              setSubmissionUrl(e.target.value);
              setUploadError('');
              setUploadSuccess(false);
            }}
            className="submission-url-input"
            placeholder={isGithubCompetition ? 'https://github.com/your-org/your-repo' : 'https://your-submission-site.example.com'}
          />
        </div>
      ) : (
        <input
          type="file"
          ref={inputRef}
          accept=".zip,application/zip"
          onChange={handleFileChange}
        />
      )}
      {manifest && (
        <p
          className={`submission-manifest submission-manifest--${manifest.mode}`}
          role="status"
        >
          <strong>
            {manifest.mode === 'graded' ? 'Graded submission'
              : manifest.mode === 'practice' ? 'Practice run'
              : 'Mode not readable'}
            {manifest.mode !== 'unknown' && manifest.runs
              ? ` \u00b7 ${manifest.runs} run${manifest.runs === 1 ? '' : 's'} per task`
              : ''}
          </strong>
          {manifest.mode === 'graded'
            ? '. This uses one of your limited submissions and runs on the held-out world. Once it starts it cannot be given back.'
            : manifest.mode === 'unknown'
              ? '. This zip does not declare a mode we recognise. If it declares a graded run, submitting will use one of your limited submissions.'
              : null}
        </p>
      )}
      {!isMultiCompetition && (
        <button
          className="btn-submit"
          onClick={handleSubmit}
          disabled={previewOnly || uploading || !isTeamLead || (isUrlCompetition ? !submissionUrl.trim() : !file)}
        >
          {uploading ? 'Uploading...' : 'Submit'}
        </button>
      )}
      {!isMultiCompetition && previewOnly && <p className="submission-preview-note">Preview only</p>}
      {uploadError && <p className="error-message" style={{ color: 'var(--error-text)' }}>{uploadError}</p>}
      {practiceNotice && (
        <p
          className={practiceNotice.kind === 'warning' ? 'submission-practice-warning' : 'submission-practice-success'}
          role={practiceNotice.kind === 'warning' ? 'status' : undefined}
        >
          {practiceNotice.text}
        </p>
      )}
      {uploadSuccess && (
        <p className="success-message" style={{ color: 'var(--success-text)' }}>
          {isMultiCompetition
            ? 'Submission package uploaded successfully!'
            : isUrlCompetition
              ? 'URL submitted successfully!'
              : 'File uploaded successfully!'}
        </p>
      )}
      {isMultiCompetition && (
        <MultiSubmissionModal
          isOpen={isMultiModalOpen}
          competitionId={competitionId}
          config={multiConfig}
          isSubmitting={uploading}
          previewOnly={previewOnly}
          onClose={() => setIsMultiModalOpen(false)}
          onSubmit={handleMultiSubmit}
        />
      )}
    </div>
  );
};

export default Submission;
